import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { 
  AlertCircle,
  Mail,
  Shield,
  ArrowRight,
  Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const HomePageVeteralia = () => {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useContext(AuthContext);
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/gestion-clientes');
    }
  }, [isAuthenticated, navigate]);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Error al iniciar sesión');
      }
      
      login(data.token, data.user);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.navBrand}>
            <img src="./Logotip_Veteralia.png" alt="Veteralia Logo" style={styles.logo} />
          </div>
        </div>
      </header>

      {/* Login Section */}
      <section style={styles.loginSection}>
        <div style={styles.loginContainer}>
          <div style={styles.loginVisual}>
            <div style={styles.loginImageContainer}>
              <img 
                src="https://images.unsplash.com/photo-1596272875729-ed2ff7d6d9c5?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="Gestión médica moderna"
                style={styles.loginImage}
              />
            </div>
            <div style={styles.loginBenefits}>
              <h3 style={styles.benefitsTitle}>Tu centro de compras</h3>
              <div style={styles.benefitsList}>
                <div style={styles.benefitPoint}>
                  <Heart size={20} color="#ACD55D" />
                  <span>Gestión automática de pedidos</span>
                </div>
                <div style={styles.benefitPoint}>
                  <Heart size={20} color="#ACD55D" />
                  <span>Reportes en tiempo real</span>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.loginFormContainer}>
            <div style={styles.loginHeader}>
              <h2 style={styles.loginTitle}>Accede a tu plataforma</h2>
              <p style={styles.loginSubtitle}>Gestiona todos tus pedidos médicos desde un solo lugar</p>
            </div>

            <form onSubmit={handleLogin} style={styles.loginForm}>
              {error && (
                <div style={styles.errorAlert}>
                  <AlertCircle size={18} />
                  {error}
                </div>
              )}

              <div style={styles.formField}>
                <div style={styles.fieldIcon}>
                  <Mail size={18} />
                </div>
                <input 
                  placeholder="Usuario" 
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  style={styles.input}
                />
              </div>

              <div style={styles.formField}>
                <div style={styles.fieldIcon}>
                  <Shield size={18} />
                </div>
                <input 
                  type="password" 
                  placeholder="Contraseña" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={styles.input}
                />
              </div>

              <button 
                type="submit" 
                style={{
                  ...styles.btnLogin,
                  ...(loading ? styles.btnLoginLoading : {})
                }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div style={styles.spinner}></div>
                    Accediendo...
                  </>
                ) : (
                  <>
                    <ArrowRight size={18} />
                    Acceder a mi cuenta
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8fafb',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(135deg, #ACD55D 0%, #95be4a 100%)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
    zIndex: 1000,
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '16px 24px',
  },
  navBrand: {
    display: 'flex',
    alignItems: 'center',
  },
  logo: {
    height: '40px',
    width: 'auto',
  },
  loginSection: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '120px 24px 80px',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e6f7ed 100%)',
  },
  loginContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '60px',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
  },
  loginVisual: {
    position: 'relative',
  },
  loginImageContainer: {
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 53, 134, 0.15)',
    marginBottom: '32px',
    height: '300px',
  },
  loginImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  loginBenefits: {
    padding: '0',
  },
  benefitsTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#003586',
    marginBottom: '24px',
  },
  benefitsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  benefitPoint: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  loginFormContainer: {
    background: 'white',
    borderRadius: '24px',
    padding: '40px',
    boxShadow: '0 20px 60px rgba(0, 53, 134, 0.1)',
    border: '2px solid #ACD55D',
  },
  loginHeader: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  loginTitle: {
    fontSize: '1.8rem',
    fontWeight: 700,
    color: '#003586',
    marginBottom: '8px',
  },
  loginSubtitle: {
    color: '#64748b',
    fontSize: '1rem',
  },
  loginForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  errorAlert: {
    background: 'rgba(245, 101, 101, 0.1)',
    color: '#f56565',
    padding: '12px 16px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.9rem',
    borderLeft: '4px solid #f56565',
  },
  formField: {
    position: 'relative',
  },
  fieldIcon: {
    position: 'absolute',
    left: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8',
    zIndex: 2,
  },
  input: {
    width: '100%',
    padding: '16px 16px 16px 48px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '16px',
    transition: 'all 0.3s ease',
    background: 'white',
    boxSizing: 'border-box',
  },
  btnLogin: {
    width: '100%',
    background: 'linear-gradient(135deg, #003586 0%, #0047b3 100%)',
    color: 'white',
    border: 'none',
    padding: '16px',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  btnLoginLoading: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  spinner: {
    width: '18px',
    height: '18px',
    border: '2px solid transparent',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

// Animación del spinner
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  input:focus {
    outline: none;
    border-color: #ACD55D !important;
    box-shadow: 0 0 0 3px rgba(172, 213, 93, 0.1) !important;
  }
  
  @media (max-width: 1024px) {
    .loginContainer {
      grid-template-columns: 1fr !important;
      gap: 40px !important;
    }
  }
  
  @media (max-width: 768px) {
    .loginSection {
      padding: 100px 20px 60px !important;
    }
    
    .loginFormContainer {
      padding: 32px 24px !important;
    }
    
    .loginImageContainer {
      height: 250px !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default HomePageVeteralia;